import type { PackageConfig, PnLResult } from '../types';
import type { BuyerProxyResult } from './buyerProxy';
import { formatPercent } from './formatters';

export interface StrategicDirection {
  name: string;
  positioningStatement: string;
  tone: string;
  leadPillar: string;
  emotionalTrigger: string;
  riskLevel: 'Wild' | 'Moderate' | 'Safe';
  rationale: string;
  targetAudience: string;
}

export function buildDirectionsPrompt(
  config: PackageConfig,
  pnl: PnLResult,
  proxy: BuyerProxyResult,
  segments: { segmentName: string; segmentRate: number; weight: number }[]
): string {
  const d = proxy.demographics;
  const featureList = config.enabledFeatures.join(', ');
  const topGoals = d.topGoals.slice(0, 3).map(g => `${g.label} (${g.percentage}%)`).join(', ');
  const topWtp = d.topWtpFactors.slice(0, 3).map(f => `${f.label} (${f.percentage}%)`).join(', ');
  const goalQuotes = d.topGoals.flatMap(g => g.sampleQuotes).slice(0, 5).map(q => `"${q}"`).join('\n  ');
  const wtpQuotes = d.topWtpFactors.flatMap(f => f.sampleQuotes).slice(0, 5).map(q => `"${q}"`).join('\n  ');
  const segLines = segments.map(s => `${s.segmentName}: ${formatPercent(s.segmentRate)} demand (${Math.round(s.weight * 100)}% of market)`).join('\n  ');
  const topRegions = d.topRegions.map(r => `${r.label} (${r.percent}%)`).join(', ');
  const topIncomes = d.topIncomes.map(i => `${i.label} (${i.percent}%)`).join(', ');

  return `You are a brand strategist generating strategic positioning directions for a health/wellness product. Your directions must be deeply rooted in the CONSUMER — who they are, what they feel, and what drives them.

Use ONLY the data provided below. Do not fabricate statistics or quotes.

PACKAGE: "${config.name}"
- Price: $${config.price}
- Features: ${featureList} (${config.enabledFeatures.length} of 7)
- Conjoint demand rate: ${formatPercent(pnl.adjustedChoiceRate)}

CONSUMER PROFILE (${proxy.count} respondents whose behavior aligns with this package):
- Gender: ${d.femalePercent}% Female, ${d.malePercent}% Male
- Age: median ${d.medianAge}, range ${d.ageRange[0]}-${d.ageRange[1]}
- Income: ${topIncomes || 'N/A'}
- Regions: ${topRegions || 'National'}
- Brand awareness: ${d.brandAwareness.neverHeardPercent}% have NEVER heard of AlterMe
- ${d.brandAwareness.positivePercent}% had positive associations, ${d.brandAwareness.negativePercent}% had negative

WHO THESE PEOPLE ARE: This is primarily a ${d.femalePercent > 60 ? 'female' : 'mixed-gender'} audience in their ${d.medianAge >= 40 ? '40s-50s' : '30s-40s'}, not a young fitness crowd. They are health-conscious adults who have likely tried and abandoned multiple wellness solutions. They are skeptical but motivated.

HEALTH GOALS — what they told us in their own words (Q1):
Top themes: ${topGoals}
Sample quotes:
  ${goalQuotes}

WHAT WOULD MAKE THEM PAY — their stated purchase drivers (Q8):
Top themes: ${topWtp}
Sample quotes:
  ${wtpQuotes}

SEGMENT DEMAND FOR THIS PACKAGE:
  ${segLines}

COMPETITIVE CONTEXT: AlterMe combines smart ring, DNA testing, body composition analysis, workouts, recovery, nutrition tracking, and adaptive wellness plans into one integrated system. Key competitors (Oura, Whoop, Noom, Peloton, InsideTracker) each own 1-2 dimensions. No competitor covers all 7.

CRITICAL INSTRUCTION: Each direction must feel like it was written FOR these specific consumers — reference their age, gender, life stage, goals, and frustrations. Do NOT write generic wellness marketing. Ground every direction in the consumer data above.

Respond with ONLY a JSON array of exactly 3 objects. No markdown, no explanation — just the JSON array.

Each object must have these exact fields:
- "name": string (2-5 word direction name)
- "positioningStatement": string (1-2 sentences — must reference the target consumer)
- "tone": string (describe the tone and voice)
- "leadPillar": string (which feature cluster leads: "Tracking & Insights", "Active Wellness", or "Personalization & Guidance")
- "emotionalTrigger": string (the primary emotion, grounded in Q1/Q8 quotes)
- "riskLevel": "Wild" | "Moderate" | "Safe"
- "rationale": string (2-3 sentences citing specific data: demographics, WTP percentages, goal themes)
- "targetAudience": string (1 sentence describing the ideal consumer for this direction, using demographic data)

Direction 1: WILD — bold, unconventional, category-creating. Speaks to the consumer's deepest frustration.
Direction 2: MODERATE — balanced sweet spot. Meets the consumer where they are with proven emotional triggers.
Direction 3: SAFE — conservative, evidence-led. Addresses the #1 WTP factor directly.

Each direction must be meaningfully distinct in tone, lead pillar, emotional trigger, AND how it speaks to the consumer.`;
}

export function parseDirectionsResponse(text: string): StrategicDirection[] {
  try {
    const jsonMatch = text.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];
    return JSON.parse(jsonMatch[0]);
  } catch {
    return [];
  }
}
